import { ChatThread } from '@/components/chat/thread';
import { useChatToken } from '@/components/chat/use-chat-token';
import { Button } from '@/components/ui/button';
import { getChatMessages } from '@/lib/api';
import { CHAT_CONTACT_EMAIL } from '@/lib/chat-contact';
import { chatMinute, chatThreadRows, mergeChatMessages } from '@/lib/chat-transcript';
import type { ChatMessage, ChatRoomView } from '@matter_extractor/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * The end, S6 and S7 (chat spec §3, §10, §11).
 *
 * The two sides diverge here more than anywhere else, so they are two
 * components rather than one with branches - and the asymmetry is the whole
 * subject:
 *
 * - **The guest gets a bare screen**: « Merci d'avoir participé », the room
 *   reference, a contact. **No summary, no analysis, no benefit.** That is a
 *   NO-GO of the pitch and not a corner left to fill in later, which is why
 *   there is a test asserting the absence rather than a comment asking for it.
 * - The reference is on that screen because §10 KEEPS the transcript and tells
 *   her so: it is what she quotes to ask for an erasure, and §12 resolves the
 *   request by searching for it. It is the only handle she has - no identity is
 *   collected (§6) and the link dies - so it is large, selectable and copiable.
 * - **The interviewer gets the transcript**, read-only, and nothing to do with
 *   it. The markdown export that used to live here moved out in issue #20: the
 *   screen where a transcript is actually re-read after the fact is
 *   `admin/entretiens.$roomId`, and that one gets its markdown SERVED by
 *   `chat/transcript.ts` off the same reading it renders. Two exports would
 *   have been two places for the paste to drift, on the artefact whose whole
 *   job is to be quotable.
 * - It renders on `closed` AND on `abandoned` (`chatRoomPhase` sends both here):
 *   a guest who left is a finding about the conduct and exactly what the cycle
 *   wants to read (§11), so it is an END and not an error - and the interviewer's
 *   side NAMES which of the two it was.
 *
 * **S7 mounts `ChatThread` and renders no bubble of its own** (issue #9). That
 * is the point of reusing it: the read-only transcript has to look like the
 * conversation it is the end of, and the marker of §11 - « Arrivé après la
 * clôture à ..., composé à ... » - is spelled once, in `message-stamps.tsx`,
 * off `closedAt`. A second thread here would be a second place for that label
 * to drift, on the one screen where the gap between the two stamps IS the
 * information.
 *
 * The presentational halves are exported so the component suite can render them
 * with rows in hand. What is left in `ChatEnding` is the fetch - see
 * `use-chat-token.ts` for why the token cannot be a prop.
 */

export interface ChatEndingGuestProps {
  room: ChatRoomView;
}

/**
 * S6 - the guest's end.
 *
 * Everything on this screen is either a thank-you, the reference, or the
 * contact. Nothing is derived from what she said. Adding a « voici ce que nous
 * avons retenu » here would be the single change this component exists to
 * prevent.
 */
export function ChatEndingGuest({ room }: ChatEndingGuestProps) {
  const [copied, setCopied] = useState<'idle' | 'copied' | 'failed'>('idle');

  function copyReference(): void {
    // `navigator.clipboard` is undefined outside a secure context, and this is
    // the LAST screen she sees: a button that silently does nothing here is the
    // memory she leaves with. So the outcome is announced either way - and the
    // reference is selectable text above it in any case.
    const { clipboard } = navigator;
    if (!clipboard) {
      setCopied('failed');
      return;
    }
    clipboard
      .writeText(room.reference)
      .then(() => setCopied('copied'))
      .catch(() => setCopied('failed'));
  }

  return (
    <section
      data-testid="chat-ending"
      data-chat-role="guest"
      className="mx-auto max-w-xl space-y-6 px-5 py-12"
    >
      <div className="space-y-2">
        <h1 className="font-semibold text-2xl text-bleu-nuit">Merci d’avoir participé</h1>
        <p className="text-base text-gray-700">
          La conversation est terminée. Elle a été conservée pour être relue par l’équipe.
        </p>
      </div>

      <div className="space-y-2 rounded-md border-2 border-gray-200 bg-gray-50 p-5">
        <p className="font-semibold text-gray-600 text-xs uppercase tracking-[0.08em]">
          Votre référence
        </p>
        {/* Large, selectable, and monospaced: it is read aloud and retyped, and
            §12 resolves an erasure request by searching for it. */}
        <p data-testid="chat-ending-reference" className="font-mono font-semibold text-2xl">
          {room.reference}
        </p>
        <p className="text-gray-700 text-sm">
          Conservez-la si vous souhaitez demander l’effacement de cette conversation.
        </p>
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={copyReference}>
            Copier la référence
          </Button>
          <a href={`mailto:${CHAT_CONTACT_EMAIL}`} className="text-bleu-nuit text-sm underline">
            {CHAT_CONTACT_EMAIL}
          </a>
        </div>
        {/* Mounted from the first render, empty or not: a live region created at
            the same moment it fills is not read out. */}
        <p aria-live="polite" className="text-gray-700 text-sm">
          {copied === 'copied' ? 'Référence copiée.' : null}
          {copied === 'failed' ? 'La copie n’a pas fonctionné. Notez-la à la main.' : null}
        </p>
      </div>

      <p className="text-gray-600 text-sm">Vous pouvez fermer cette page.</p>
    </section>
  );
}

export interface ChatEndingInterviewerProps {
  room: ChatRoomView;
  /** In the order of ARRIVAL, exactly as the store served them (§8.2). */
  messages: readonly ChatMessage[];
  /** True while the transcript is still on its way. */
  loading: boolean;
  /**
   * True when the transcript could not be read. The end screen still renders:
   * the closure happened, and a failed read of the history is not a reason to
   * show the interviewer an error page over a finished interview.
   */
  failed: boolean;
}

/**
 * S7 - the interviewer's end: the thread in READ-ONLY, and that is all.
 *
 * There is no composer, and there is no control that would put one back. The
 * one-way door of §4.5 runs in this direction too: a screen that could reopen a
 * conversation somebody was told had ended would make the guest's end screen a
 * lie.
 *
 * `chatThreadRows(messages, [])` - the second argument is the client-side queue
 * of §8, and it is EMPTY by construction here: a closed room has no composer to
 * queue from, so a pending row on this screen would be a message the person can
 * never send.
 */
export function ChatEndingInterviewer({
  room,
  messages,
  loading,
  failed,
}: ChatEndingInterviewerProps) {
  /**
   * Which end this was, and the distinction §11 asks for by name.
   *
   * « L'invité est parti » is a constat sur la conduite and exactly what the
   * cycle wants to read - so `abandoned` is worded as itself rather than folded
   * into « terminée ». The LIVE reading of « déconnecté » versus « silencieux »
   * belongs to S4, off `chatPeerPresence` (issue #9); what is left to say here
   * is which of the two ends actually happened.
   */
  const ended =
    room.status === 'abandoned' ? 'L’invité est parti avant la fin.' : 'Conversation terminée.';

  return (
    // No forced viewport height: the SPA already puts its own banner above this
    // screen, so a `h-dvh` section starts below the fold and ends past it -
    // which pushes the footer, and therefore the export and the reference, out
    // of a viewport capture. The thread is capped instead and scrolls inside
    // itself, so header, thread and footer are all on screen at once.
    <section data-testid="chat-ending" data-chat-role="interviewer" className="flex flex-col">
      <header className="flex flex-wrap items-center justify-between gap-3 border-gray-200 border-b px-8 py-3">
        <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 font-semibold text-gray-600 text-sm">
          Lecture seule
        </span>
        <span className="text-gray-600 text-sm">
          {room.closedAt === null ? 'Terminé' : `Terminé à ${chatMinute(room.closedAt)}`},{' '}
          {messages.length} message{messages.length > 1 ? 's' : ''}
        </span>
      </header>

      {/* Capped and scrolling INSIDE itself, which is what keeps the footer on
          screen - and what makes a capture in viewport show the end of the
          conversation rather than the top of a page taller than the frame.
          `e2e/CLAUDE.md` requires the viewport shot; this is what makes one
          worth taking. */}
      <div className="flex max-h-[62vh] flex-col px-8 py-6">
        {loading ? <p className="text-gray-600 text-sm">Lecture du fil...</p> : null}
        {failed ? (
          <p className="text-rouge-deep text-sm">
            Le fil n’a pas pu être relu. L’entretien est bien clôturé.
          </p>
        ) : null}
        {!loading && !failed && messages.length === 0 ? (
          // « Jamais ouverte » is a displayed state and not a blank (§12).
          <p data-testid="chat-ending-empty" className="text-gray-600 text-sm">
            Aucun message : la conversation n’a jamais commencé.
          </p>
        ) : null}

        {messages.length > 0 ? (
          // `room.role` and not the literal `"interviewer"`: this screen only
          // ever renders for that role, but a literal on a `role` prop is read
          // by the a11y lint as an ARIA role and refused. Passed the same way
          // `conversation-panel.tsx` passes it.
          <ChatThread
            rows={chatThreadRows(messages, [])}
            role={room.role}
            closedAt={room.closedAt}
          />
        ) : null}
      </div>

      {/* What is left in the footer is what S7 is FOR: which of the two ends
          this was, and the reference that names the room in a ticket. The
          export left in issue #20 and took its `aria-live` acknowledgement
          with it - there is no longer an outcome here to announce. */}
      <footer className="flex flex-wrap items-center justify-end gap-4 border-gray-200 border-t px-8 py-3 text-gray-600 text-sm">
        <span data-testid="chat-ending-outcome">{ended}</span>
        <span data-testid="chat-ending-reference" className="font-mono">
          Référence {room.reference}
        </span>
      </footer>
    </section>
  );
}

export interface ChatEndingProps {
  room: ChatRoomView;
}

/**
 * The mounted end screen: the role picks the side, and only the interviewer
 * reads the transcript.
 *
 * The guest's side fetches NOTHING, and that is the shape of « aucun bénéfice
 * rendu » in code: there is no call she could make whose answer a future screen
 * would be tempted to render.
 */
export function ChatEnding({ room }: ChatEndingProps) {
  const token = useChatToken();
  const queryClient = useQueryClient();
  const interviewer = room.role === 'interviewer';
  /**
   * The transcript, under the SAME key and in the SAME shape as
   * `conversation-panel.tsx` writes it: a `ChatMessage[]`.
   *
   * That agreement is the whole fix of issue #19, and the defect it closes was
   * worth more than the two lines it took. Both components read
   * `['chat', <jeton>, 'messages']`, but this one used to expect the transport's
   * `{ messages }` while the panel had filled the key with a bare array - so
   * `data.messages` was `undefined`, the fallback `[]` won, and S7 announced
   * « la conversation n'a jamais commencé » on a room that had just carried
   * three. Nothing caught it either: the global `staleTime` of 30 s made the
   * cached value FRESH at mount, so no request went out to correct the shape,
   * and the screen stayed empty until the tab was reloaded.
   *
   * The panel's shape is the one that survives because it is the constrained
   * one - it carries the incremental read and the merge. This screen is the
   * simple case: one read of a finished conversation, whole, no `since`.
   *
   * `refetchOnMount: 'always'` is the second half, and it is not belt and
   * braces. The panel keeps socket deltas in React state and merges them at
   * RENDER rather than writing them into the cache (a `setQueryData` racing an
   * in-flight refetch loses a message), so at the instant of the closure the
   * cached array can be one beat behind what was on screen. A guaranteed read
   * here is what makes the last thing she said appear on the transcript of the
   * conversation that just ended - and it costs one request, once, on a screen
   * that never polls.
   */
  const transcript = useQuery({
    queryKey: ['chat', token, 'messages'],
    queryFn: async (): Promise<ChatMessage[]> => {
      const known = queryClient.getQueryData<ChatMessage[]>(['chat', token, 'messages']) ?? [];
      const answer = await getChatMessages(token);
      // Merged rather than substituted: `mergeChatMessages` collapses on the
      // key of §8.1, so a message the panel only ever saw on the socket is kept
      // even in the window before the store has served it back.
      return mergeChatMessages(known, answer.messages);
    },
    enabled: interviewer,
    refetchOnMount: 'always',
  });

  if (!interviewer) return <ChatEndingGuest room={room} />;
  return (
    <ChatEndingInterviewer
      room={room}
      messages={transcript.data ?? []}
      // `isPending` alone would flash the EMPTY state on the way in: with a
      // cached array in hand the query is not pending, but the guaranteed
      // refetch above is still out - and an array that is merely stale can be
      // empty. So a fetch with nothing to show yet counts as loading, while a
      // fetch behind a thread already on screen does not: « Lecture du fil... »
      // over the conversation it is re-reading would be noise.
      loading={
        transcript.isPending || (transcript.isFetching && (transcript.data ?? []).length === 0)
      }
      failed={transcript.isError}
    />
  );
}
